import type { ReactNode } from 'react';

/**
 * System identifiers for top-level navigation groups.
 * Each system represents a major functional area of the IT Service Desk.
 */
export type SystemId = 'ticketing' | 'knowledge-base' | 'admin';

/**
 * View identifiers within the Ticketing system.
 */
export type TicketingViewId = 'my-work' | 'all-tickets';

/**
 * View identifiers within the Knowledge Base system.
 */
export type KnowledgeBaseViewId = 'articles' | 'categories' | 'search';

/**
 * View identifiers within the Admin system.
 */
export type AdminViewId = 'users' | 'tech-status' | 'stats' | 'analytics';

/**
 * Union of all view identifiers across systems.
 */
export type ViewId = TicketingViewId | KnowledgeBaseViewId | AdminViewId;

/**
 * Represents a single navigation item (leaf node) in the sidebar.
 */
export interface NavItem {
  /** Unique identifier for the nav item */
  id: string;
  /** Display label shown in the sidebar */
  label: string;
  /** Icon component to display */
  icon: ReactNode;
  /** Route path for navigation */
  path: string;
  /** Optional badge content (e.g., notification count) */
  badge?: number | string;
  /** Whether this item is disabled */
  disabled?: boolean;
}

/**
 * Represents a navigation group (parent node) containing child items.
 * Groups can be expanded/collapsed to show/hide their items.
 */
export interface NavGroup {
  /** Unique identifier matching a SystemId */
  id: SystemId;
  /** Display label for the group header */
  label: string;
  /** Icon component for the group */
  icon: ReactNode;
  /** Base path for the system (e.g., '/ticketing') */
  basePath: string;
  /** Child navigation items */
  items: NavItem[];
  /** Whether this group should be expanded by default */
  defaultExpanded?: boolean;
}

/**
 * Complete navigation configuration for the sidebar.
 */
export interface NavigationConfig {
  /** Array of navigation groups (systems) */
  systems: NavGroup[];
  /** Optional footer items (e.g., Settings, Help) */
  footerItems?: NavItem[];
}

/**
 * Context value for navigation state management.
 */
export interface NavigationContextValue {
  /** Whether the sidebar is in collapsed (icons-only) mode */
  isCollapsed: boolean;
  /** Set of currently expanded group IDs */
  expandedGroups: Set<string>;
  /** Currently active system ID derived from URL */
  activeSystemId: SystemId | null;
  /** Currently active view ID derived from URL */
  activeViewId: ViewId | null;
  /** Whether the mobile drawer is open */
  isMobileDrawerOpen: boolean;
  /** Toggle sidebar between collapsed and expanded */
  toggleCollapsed: () => void;
  /** Set collapsed state directly */
  setCollapsed: (collapsed: boolean) => void;
  /** Toggle a group's expanded state */
  toggleGroup: (groupId: string) => void;
  /** Expand a specific group */
  expandGroup: (groupId: string) => void;
  /** Collapse a specific group */
  collapseGroup: (groupId: string) => void;
  /** Set mobile drawer open state */
  setMobileDrawerOpen: (open: boolean) => void;
}

/**
 * Layout dimension constants used across layout components.
 */
export const LAYOUT_DIMENSIONS = {
  SIDEBAR_WIDTH_EXPANDED: 240,
  SIDEBAR_WIDTH_COLLAPSED: 64,
  TOPBAR_HEIGHT: 48,
  TRANSITION_DURATION: 200,
} as const;
